<template>
  <div class="submissions-mobile-view" data-cy="submissions-mobile-view">
    <section
      class="submissions-mobile-view__header-section"
      data-cy="submissions-mobile-view-header-section"
    >
      <div class="sort-wrapper">
        <DropDownList
          class="sort-dropdown"
          :textField="'text'"
          :data-items="sortOptions"
          v-model="selectedSortOption"
          @change="onSortChanged"
          data-cy="sort-dropdown"
        />
      </div>
      <span
        class="filter-icon"
        @click="showFilters = !showFilters"
        data-cy="filters-button"
      >
        <FilterIcon />
      </span>
    </section>
    <SubmissionFiltersMenu
      v-show="showFilters"
      :getPropValues="getPropValuesAsync"
      :filters="submissionFilters"
      @apply="onFiltersApplied"
      @clear="onFiltersCleared"
      @close="showFilters = false"
    />
    <div class="submissions-mobile-view__date-wrapper">
      <div>
        <label for="date-start">Start</label>
        <DatePicker
          id="date-start"
          class="submissions-mobile-view__date-picker"
          :value="dateRange.start!"
          :max="dateRange.end || new Date()"
          @change="dateRange.start = $event.value"
          data-cy="date-range-picker"
        />
      </div>
      <div>
        <label for="date-end">End</label>
        <DatePicker
          id="date-end"
          class="submissions-mobile-view__date-picker"
          :value="dateRange.end!"
          :max="new Date()"
          :min="dateRange.start || undefined"
          @change="dateRange.end = $event.value"
          data-cy="date-range-picker"
        />
      </div>

      <Button
        :theme-color="'primary'"
        type="button"
        class="button-by-date"
        @click="filterByDate"
        data-cy="by-date"
        >By Date</Button
      >
    </div>
    <SubmissionList
      :totalSubmissions="totalSubmissions"
      :submissions="submissions"
      :defaultTake="submissionsTake"
      :skip="submissionsSkip"
      :loading="submissionsLoading"
      @submissionsChanged="onSubmissionsChanged"
    />
  </div>
</template>

<script setup lang="ts">
import FilterIcon from "@/icons/FilterIcon.vue";
import { useMainStore } from "@/stores/useMainStore";
import { onBeforeMount, ref } from "vue";
import { api } from "@/api";
import SubmissionList from "./SubmissionList.vue";
import SubmissionFiltersMenu from "./SubmissionFiltersMenu.vue";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import type { FilterExpression } from "@/Domain/FilterExpression";
import type { FilterField } from "@/Domain/FilterField";
import type { FilterGroup } from "@/Domain/FilterGroup";
import {
  FilterValuesTake,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import type { GetSubmissionsWithFilteringRequest } from "@/api/submissionsOverview";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import moment from "moment";
import type {
  SelectionRange,
} from "@progress/kendo-vue-dateinputs";
import { Button } from "@progress/kendo-vue-buttons";
import { DatePicker } from "@progress/kendo-vue-dateinputs";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";

const main = useMainStore();
const totalSubmissions = ref<number>(0);
const submissions = ref<SubmissionsTable[]>([]);
const submissionsLoading = ref<boolean>(false);
const submissionsTake = ref<number>(25);
const submissionsSkip = ref<number>(0);
const showFilters = ref<boolean>(false);
const submissionFilters = ref<FilterMenuItem[]>([]);

const currentDate = new Date();
const startDate = moment().subtract(1, "month").toDate();

const dateRange = ref({
  start: new Date(startDate),
  end: new Date(currentDate),
} as SelectionRange);

const sortOptions = [
  {
    text: "Sort By",
    value: "",
  },
  {
    text: "Job ID",
    value: "JobID",
  },
  {
    text: "Job Type",
    value: "JobType",
  },
  {
    text: "Status",
    value: "Status",
  },
  {
    text: "Date",
    value: "DateProcessedForSort",
  },
  {
    text: "Submitted By",
    value: "UserLogin",
  },
];

const selectedSortOption = ref<{ text: string; value: string }>(sortOptions[0]);

const valueFiltersConvertOptions = [
  {
    column: "DateProcessedForSort",
    convert: (value: string) => {
      return new Date(value).toLocaleDateString();
    },
  },
] as ValueFieldConvertOption[];

const getSubmissionsWithFilteringAsync = async function (
  skip: number,
  take: number,
  valuesForFieldOnly: string = ""
) {
  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  submissionFilters.value.forEach((f) => {
    const filterGroup = {
      FilterFields: [],
      BooleanLogicOperator: EnumFilterStatementConnector.And,
      FieldName: f.field,
      InternalFieldName: "",
    } as FilterGroup;
    const filterField = {
      Operator: f.getOpValue ? f.getOpValue(f.value) : "eq",
      Value: f.getFilterValue ? f.getFilterValue(f.value) : f.value,
    } as FilterField;
    filterGroup.FilterFields.push(filterField);

    filterModel.FilterGroups.push(filterGroup);
  });

  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    filters: JSON.stringify(filterModel),
    beginDate: dateRange.value.start?.toLocaleDateString("en-US"),
    endDate: dateRange.value.end?.toLocaleDateString("en-US"),
    skip: skip,
    take: take,
    sortField: selectedSortOption.value.value,
    ascending: true,
    valuesForFieldOnly: valuesForFieldOnly,
    iarLogin: "",
  } as GetSubmissionsWithFilteringRequest;
  const endpoint = "GetSubmissionsWithFiltering";

  if (!valuesForFieldOnly) return api.get(endpoint, requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;
  requestParams.sortField = "";
  requestParams.filters = "";

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, endpoint),
    () => api.get(endpoint, requestParams)
  );
};

const onSubmissionsChanged = function (skip: number, take: number) {
  submissionsTake.value = take;
  submissionsSkip.value = skip;
  getSubmissionsAsync(skip, true);
};

const onFiltersApplied = function (filters: FilterMenuItem[]) {
  submissionFilters.value = filters;
  submissionsSkip.value = 0;
  getSubmissionsAsync();
};

const onSortChanged = function () {
  submissionsSkip.value = 0;
  getSubmissionsAsync();
};

const onFiltersCleared = function () {
  submissionFilters.value = [];
  submissionsSkip.value = 0;
  getSubmissionsAsync();
};

const getSubmissionsAsync = async function (
  skip: number = 0,
  appendProducts: boolean = false
) {
  if (submissionsLoading.value) return;
  submissionsLoading.value = true;
  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(skip, submissionsTake.value),
    (data) => {
      if (!appendProducts) submissions.value = data.ListItems;
      else submissions.value = [...submissions.value, ...data.ListItems];
      totalSubmissions.value = data.TotalItemsInList;
    },
    "SubmissionsMobileView.vue",
    "getSubmissionsAsync"
  );
  submissionsLoading.value = false;
};

const getPropValuesAsync = async function (field: string) {
  let items: SubmissionsTable[] = [];
  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(0, 999999, field),
    (data) => {
      items = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, field, items);
    },
    "SubmissionsMobileView.vue",
    "getPropValuesAsync"
  );
  return items;
};

const filterByDate = function () {
  submissionsSkip.value = 0;
  getSubmissionsAsync();
};

onBeforeMount(async () => {
  await getSubmissionsAsync();
});
</script>

<style lang="scss">
.submissions-mobile-view {
  &__header-section {
    background: #222;
    border-top: 1px solid #333;
    display: flex;
    align-items: center;
    padding: 10px;

    .sort-wrapper {
      position: relative;
      display: flex;
      flex: 1;

      .sort-dropdown {
        width: 100%;
        max-width: 340px;
      }
    }

    .filter-icon {
      margin-left: 6px;
      cursor: pointer;
      padding: 5px 15px;
    }
  }

  &__date-wrapper {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    padding: 10px;
  }

  .button-by-date {
    height: 36px;
  }
}
</style>
